import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ErrorHandlerService } from '@libs/shared/common';
import { AzureLoginResult, resetPasswordRequested } from '@libs/shared/models';
import { from, map, Observable, throwError } from 'rxjs';
import { AzureOAuthNative } from './azure';
import { OAuthService } from './oauth.service';

interface NativeError {
  errorMessage?: string;
  code?: string;
}

@Injectable()
export class OAuthAppService extends OAuthService {
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
  private readonly document: Document = inject(DOCUMENT);

  public override login(): Observable<AzureLoginResult> {
    return from(this.loginHandler()).pipe(map(result => ({ ...result, type: 'user' })));
  }

  /**
   * The native oauth plugin has no sign-up method yet, registration is only available in the web app.
   */
  public override register(): Observable<AzureLoginResult> {
    return throwError(() => new Error('Registration is not supported in the native app'));
  }

  public override logout(): Observable<void> {
    return from(this.logoutHandler());
  }

  public override refreshToken(): Observable<AzureLoginResult | undefined> {
    return from(AzureOAuthNative.refreshToken(this.configService.getUserAzureOAuthOptions())).pipe(map(token => ({
      ...token,
      type: 'user',
    })));
  }

  private async logoutHandler(): Promise<void> {
    try {
      await AzureOAuthNative.logout(this.configService.getUserAzureOAuthOptions());
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.document.defaultView!.location.reload();
    } catch (e: unknown) {
      const error: Error = this.mapNativeError(e);

      this.errorHandler.handleError(error);
    }
  }

  private async loginHandler(): Promise<AzureLoginResult> {
    try {
      return await AzureOAuthNative.login(this.configService.getUserAzureOAuthOptions());
    } catch (e: unknown) {
      const error: Error = this.mapNativeError(e);

      if (error.message === resetPasswordRequested) {
        this.handleResetPasswordRequest();
      }

      throw error;
    }
  }

  private mapNativeError(error: unknown): Error {
    const nativeError: NativeError = error as NativeError;

    return new Error(nativeError.errorMessage ?? nativeError.code);
  }

  private handleResetPasswordRequest(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.document.defaultView!.open(this.configService.buildResetPasswordUrl(), '_blank');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    void this.document.defaultView!.location.reload();
  }
}
